
import { motion } from "framer-motion";
import { Suspense, lazy, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import HeroSection from "@/components/home/HeroSection";
import USPSection from "@/components/home/USPSection";
import FeaturedTestimonial from "@/components/home/FeaturedTestimonial";
import HowItWorks from "@/components/home/HowItWorks";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import { PricingSection } from "@/components/home/PricingSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { PageSEO } from "@/components/SEO/PageSEO";
import SubscriptionFeatureTable from "@/components/subscription/SubscriptionFeatureTable";

// Lazy load components for better performance
const CategoriesSection = lazy(() => import("@/components/home/CategoriesSection"));
const MapSection = lazy(() => import("@/components/home/MapSection"));
const FeaturedProviders = lazy(() => import("@/components/home/FeaturedProviders"));
const FAQSection = lazy(() => import("@/components/home/FAQSection"));
const CTASection = lazy(() => import("@/components/home/CTASection"));

// Enhanced loading fallback component with consistent styling and smaller size on mobile
const LoadingFallback = ({ height = '40vh', text }: { height?: string, text: string }) => {
  const isMobile = useIsMobile();
  
  return (
    <div 
      className={`py-6 md:py-16 flex flex-col items-center justify-center`} 
      style={{ height: isMobile ? 'auto' : height }}
    >
      <div className="space-y-4 w-full max-w-3xl px-4">
        <Skeleton className="h-5 md:h-8 w-3/4 mx-auto rounded-lg bg-[#212121]/50" />
        <Skeleton className="h-3 md:h-4 w-1/2 mx-auto rounded-lg bg-[#212121]/50" />
        <div className="flex justify-center mt-4">
          <div className="inline-block p-3 md:p-4 rounded-full bg-[#212121]/30 animate-pulse">
            <div className="h-5 w-5 md:h-8 md:w-8 border-t-2 border-b-2 border-[#2C6E49] rounded-full animate-spin"></div>
          </div>
        </div>
        <p className="mt-2 text-[#F8F8F8]/50 text-sm md:text-base text-center">{text}</p>
      </div>
    </div>
  );
};

// Meta component for SEO (hidden in the UI)
const PageMeta = () => {
  return (
    <div className="sr-only" aria-hidden="true">
      <h1>CHRONA - Dein Termin. Einfach gebucht.</h1>
      <p>Buche einfach und schnell deinen Wunschtermin bei Friseuren, Nagelstudios, Coaches und mehr.</p>
    </div>
  );
};

// Feature Matrix Section Component
const FeatureMatrixSection = () => {
  return (
    <section className="py-12 md:py-20 bg-[#1A1A1A]">
      <div className="container mx-auto px-4">
        <motion.div 
          className="mb-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-[#F8F8F8] relative inline-block">
            <span className="relative z-10">Übersicht der Funktionen</span>
            <span className="absolute -bottom-1 left-0 w-full h-1 bg-[#2C6E49] opacity-70"></span>
          </h2>
          <p className="mt-3 text-[#F8F8F8]/70 max-w-2xl mx-auto">
            Finde das passende Abo für dein Studio oder Salon
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-[#191919]/80 p-4 md:p-6 rounded-xl border border-[#F8F8F8]/10"
        >
          <SubscriptionFeatureTable />
        </motion.div>
      </div>
    </section>
  );
};

const Home = () => {
  const isMobile = useIsMobile();
  
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Define structured data for homepage
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "CHRONA – Dein Termin. Deine Zeit.",
    "url": "https://www.chrona.it",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.chrona.it/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "description": "Finde, buche und verwalte Beauty-Termine in ganz Deutschland – schnell, einfach, mobil."
  };
  
  return (
    <motion.div 
      className="flex flex-col bg-[#212121] pb-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Enhanced SEO Component */}
      <PageSEO
        title="CHRONA - Dein Termin. Deine Zeit."
        description="Finde, buche und verwalte Beauty-Termine in ganz Deutschland – schnell, einfach, mobil."
        keywords="Termine, Beauty, Spa, Massage, Friseur, Buchung, Studio, Kundenportal, Kalender"
        canonicalPath="/"
        ogImage="/lovable-uploads/bbbd313b-d6bd-4b33-af58-258cd00a4dc3.png"
        ogType="website"
        twitterCard="summary_large_image"
        twitterSite="@chrona_app"
        structuredData={homepageStructuredData}
      />
      
      {/* SEO Meta information */}
      <PageMeta />
      
      {/* Hero Section with Search */}
      <HeroSection />
      
      {/* USP Section - Load directly for better UX */}
      <USPSection />
      
      {/* Categories Section - Now with proper icons */}
      <Suspense fallback={<LoadingFallback text="Kategorien werden geladen..." />}>
        <CategoriesSection />
      </Suspense>
      
      {/* Feature Matrix Section */}
      <FeatureMatrixSection />
      
      {/* Featured Testimonial - New section */}
      <FeaturedTestimonial />
      
      {/* Map Section - Now with Mapbox integration */}
      <Suspense fallback={<LoadingFallback text="Karte wird geladen..." height={isMobile ? "300px" : "500px"} />}>
        <MapSection />
      </Suspense>
      
      {/* Featured Providers */}
      <Suspense fallback={<LoadingFallback text="Anbieter werden geladen..." />}>
        <FeaturedProviders />
      </Suspense>
      
      {/* Testimonials Section */}
      <TestimonialsSection />
      
      {/* Pricing Section - Set initial state to avoid errors */}
      <PricingSection
        initialTab="customers"
        onTabChange={() => {}}
        onPlanSelect={() => {}}
        showLoginButton={true}
      />
      
      {/* FAQ Section */}
      <Suspense fallback={<LoadingFallback text="Wird geladen..." />}>
        <FAQSection />
      </Suspense>
      
      {/* Call to Action Section */}
      <Suspense fallback={<LoadingFallback text="Wird geladen..." />}>
        <CTASection />
      </Suspense>
    </motion.div>
  );
};

export default Home;
